import { Typography } from '@execonline-inc/execonline-ui';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { T, TPlainTextKey } from '../../../../../../Translations';
import { useCloneProgress } from './useCloneProgress';

const STEPS: ReadonlyArray<TPlainTextKey> = [
  'Duplicating collection...',
  'Copying courses...',
  'Organizing sections...',
  'Finalizing...',
];

const SKELETON_LINE_WIDTHS = ['100%', '80%', '100%', '65%'];

interface Props {
  collectionName: string;
}

function CloneProgress({ collectionName }: Props) {
  const { stepIndex, progressPct, tile, landedSlots } = useCloneProgress(STEPS.length);

  return (
    <div className="flex w-full flex-col items-center pb-2 pt-4 text-center">
      <div className="relative mb-7" style={{ width: 320, height: 130 }}>
        <div
          className="absolute border-t-2 border-dashed border-border"
          style={{ left: 108, right: 108, top: 64 }}
        />

        <div
          className="absolute rounded-xl border border-border bg-white p-3"
          style={{ left: 0, top: 20, width: 92, height: 108, boxShadow: 'var(--elevation-sm)' }}
        >
          <div className="h-2 rounded bg-primary" style={{ width: 26 }} />
          {SKELETON_LINE_WIDTHS.map((width, i) => (
            <div
              key={i}
              className={clsx('h-1.5 rounded bg-muted', i === 0 ? 'mt-3' : 'mt-2')}
              style={{ width }}
            />
          ))}
        </div>

        <div
          className="absolute rounded-xl border-2 border-dashed border-teal-200 bg-secondary p-3"
          style={{ right: 0, top: 20, width: 92, height: 108 }}
        >
          <div
            className={clsx(
              'h-2 rounded transition-colors duration-200',
              landedSlots[0] ? 'bg-primary' : 'bg-muted',
            )}
            style={{ width: 26 }}
          />
          {SKELETON_LINE_WIDTHS.map((width, i) => (
            <div
              key={i}
              className={clsx(
                'h-1.5 rounded transition-colors duration-200',
                i === 0 ? 'mt-3' : 'mt-2',
                landedSlots[i + 1] ? 'bg-teal-200' : 'bg-muted',
              )}
              style={{ width }}
            />
          ))}
        </div>

        <div
          className="absolute rounded-md border-2 border-primary bg-white"
          style={{
            top: 52,
            left: tile.left,
            width: 24,
            height: 30,
            opacity: tile.opacity,
            transform: `translateY(${tile.arc}px) rotate(${tile.rotation}deg)`,
            boxShadow: '0 6px 14px rgba(24, 122, 144, 0.35)',
          }}
        >
          <div className="mx-auto mt-1.5 h-1 w-3 rounded-sm bg-primary" />
          <div className="mx-auto mt-1 h-1 w-2 rounded-sm bg-teal-200" />
        </div>
      </div>

      <Typography variant="h5" fontWeight="semibold" color="black">
        <T kind="Cloning your collection" />
      </Typography>
      <div className="mt-1.5 text-sm text-muted-foreground">
        <T
          kind="Copying <name/>"
          name={<span className="font-semibold text-primary">{collectionName}</span>}
        />
      </div>

      <div className="mt-4 h-5 text-sm text-muted-foreground">
        <T kind={STEPS[stepIndex]} />
      </div>

      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${progressPct}%`, transition: 'width 0.2s linear' }}
        />
      </div>

      <div className="mt-4 text-xs text-muted-foreground">
        <T kind="Hang tight — this may take a minute or two" />
      </div>
    </div>
  );
}

export default observer(CloneProgress);
